import { FC, HTMLProps, UIEvent, useState } from 'react';
import { Typography } from '@mui/material';
import classNames from 'classnames';
import { CustomButton } from 'components/shared';
import { getAmountTitle, getPaymentWordByCount } from 'utils/helpers';
import { ReceiptApartment } from 'utils/types';
import { ReceiptMap } from '../ReceiptMap';
import styles from './index.module.scss';

interface ReceiptPreview extends HTMLProps<HTMLDivElement> {
	isDisabled?: boolean;
	apartmentData: ReceiptApartment | null;
	amountToPay: number;
	onClickPay: () => void;
	isDisabledActions?: boolean;
	isDisabledMap?: boolean;
	selectedServicesCount?: number;
	apartmentAccountId?: number | null;
	isHidePayButton?: boolean;
	mapTitle?: string;
	mapSubtitle?: string;
	onMapBack?: () => void;
	isHideMapMenu?: boolean;
	payButtonLabel?: string;
	isHidePayButtonSummary?: boolean;
	isHidePaymentNote?: boolean;
}

const APARTMENT_MAP_MIN_HEIGHT = 116;

export const ReceiptPreview: FC<ReceiptPreview> = ({
	children,
	apartmentData,
	isDisabled,
	amountToPay,
	onClickPay,
	isDisabledActions,
	className,
	isDisabledMap,
	selectedServicesCount = 0,
	apartmentAccountId,
	isHidePayButton = false,
	mapTitle,
	mapSubtitle,
	onMapBack,
	isHideMapMenu = false,
	payButtonLabel = 'Сплатити',
	isHidePayButtonSummary = false,
	isHidePaymentNote = false,
}) => {
	const [isMapCollapsed, setMapCollapsed] = useState(false);

	const handleScroll = (event: UIEvent<HTMLDivElement>): void => {
		const { currentTarget } = event;
		const { scrollTop, scrollHeight, clientHeight } = currentTarget;

		if (scrollTop <= 0) {
			setMapCollapsed(false);
		} else if (!isMapCollapsed && scrollHeight - clientHeight > APARTMENT_MAP_MIN_HEIGHT + 50) {
			setMapCollapsed(true);
		}
	};

	return (
		<div
			className={classNames(styles.content, className, {
				[styles.content_collapsed]: isMapCollapsed,
				[styles.content_mapDisabled]: isDisabledMap,
			})}
		>
			{!isDisabledMap && (
				<ReceiptMap
					apartmentAccountId={apartmentAccountId}
					apartment={apartmentData}
					isDisabledActions={isDisabledActions}
					isCollapsed={isMapCollapsed}
					title={mapTitle}
					subtitle={mapSubtitle}
					onBack={onMapBack}
					isHideMenu={isHideMapMenu}
				/>
			)}
			<div className={styles.receipt}>
				<div className={styles.receipt__list} onScroll={isDisabledMap ? undefined : handleScroll}>
					{children}
				</div>
				{!isHidePayButton && (
					<div className={styles.button}>
						<CustomButton
							type="button"
							onClick={onClickPay}
							disabled={isDisabled}
							size="large"
						>
							<div className={styles.btn}>
								<Typography component="span" color="inherit" className={styles.btn__text}>
									{payButtonLabel}
								</Typography>

								{!isHidePayButtonSummary && (
									<Typography
										component="span"
										color="inherit"
										className={classNames(styles.btn__text, styles.amount)}
									>
										{selectedServicesCount} {getPaymentWordByCount(selectedServicesCount)}{' '}
										{getAmountTitle(amountToPay)}
									</Typography>
								)}
							</div>
						</CustomButton>
						{!isHidePaymentNote && (
							<div className={styles.legal}>
								<Typography variant="caption" color="inherit">
									ТОВ «ХОСТПЕЙ», ЄДРПОУ: 45341126, Ліцензія НБУ №21/155-PK від 26.02.2024
								</Typography>
								<div className={styles.note}>
									<Typography variant="caption" color="inherit">
										{` Операція за участю`}
									</Typography>
									<a href={process.env.REACT_APP_FINANSO_UA_URL} target="_blank" rel="noreferrer">
										{` ТОВ "ФІНАНСО УА"`}
									</a>
								</div>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};
